import { PageEditor } from "../components/PageEditor";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    AlertTriangle,
    Edit,
    FileText,
    Filter,
    Image,
    LayoutGrid,
    List,
    Plus,
    Search,
    Settings,
    Trash} from "lucide-react";
import { useState } from "react";

// Моки даних для прикладу
const mockPages = [
    {
        id: 1,
        title: "Головна сторінка",
        slug: "home",
        status: "published",
        lastModified: "2024-03-15T10:00:00",
        author: "John Doe"
    },
    {
        id: 2,
        title: "Про нас",
        slug: "about",
        status: "draft",
        lastModified: "2024-03-14T15:30:00",
        author: "Jane Smith"
    },
    {
        id: 3,
        title: "Контакти",
        slug: "contacts",
        status: "published",
        lastModified: "2024-03-13T09:45:00",
        author: "John Doe"
    }
];

const mockMedia = [
    {
        id: 1,
        name: "hero-image.jpg",
        type: "image",
        size: "1.2 MB",
        dimensions: "1920x1080",
        uploadedAt: "2024-03-15T10:00:00"
    },
    {
        id: 2,
        name: "about-video.mp4",
        type: "video",
        size: "15.4 MB",
        dimensions: "1920x1080",
        uploadedAt: "2024-03-14T15:30:00"
    },
    {
        id: 3,
        name: "brochure.pdf",
        type: "document",
        size: "2.8 MB",
        uploadedAt: "2024-03-13T09:45:00"
    }
];

const CMSPage = () => {
    const [activeTab, setActiveTab] = useState("pages");
    const [viewMode, setViewMode] = useState<"grid" | "list">("list");
    const [searchQuery, setSearchQuery] = useState("");
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [selectedPage, setSelectedPage] = useState<any>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<any>(null);

    const getStatusColor = (status: string) => {
        switch (status) {
            case "published":
                return "bg-emerald-500/15 text-emerald-600";
            case "draft":
                return "bg-amber-500/15 text-amber-600";
            default:
                return "bg-slate-500/15 text-slate-600";
        }
    };

    const handleEdit = (page: any) => {
        setSelectedPage(page);
        setIsEditorOpen(true);
    };

    const handleDelete = (page: any) => {
        setPageToDelete(page);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        // Тут буде логіка видалення
        console.log("Deleting page:", pageToDelete);
        setIsDeleteDialogOpen(false);
        setPageToDelete(null);
    };

    return (
        <div className="container mx-auto p-8 space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Управління контентом</h1>
                    <p className="text-muted-foreground">
                        Створення та редагування контенту сайту
                    </p>
                </div>
                <Button variant="outline" size="icon">
                    <Settings className="h-5 w-5" />
                </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Всього сторінок
                        </CardTitle>
                        <FileText className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">24</div>
                        <p className="text-xs text-muted-foreground">
                            18 опубліковано, 6 чернеток
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Медіафайли
                        </CardTitle>
                        <Image className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">156</div>
                        <p className="text-xs text-muted-foreground">
                            Загальний розмір: 2.4 GB
                        </p>
                    </CardContent>
                </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <div className="flex items-center justify-between">
                    <TabsList>
                        <TabsTrigger value="pages">Сторінки</TabsTrigger>
                        <TabsTrigger value="media">Медіафайли</TabsTrigger>
                        <TabsTrigger value="settings">Налаштування</TabsTrigger>
                    </TabsList>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setViewMode("grid")}
                                className={viewMode === "grid" ? "bg-accent" : ""}
                            >
                                <LayoutGrid className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setViewMode("list")}
                                className={viewMode === "list" ? "bg-accent" : ""}
                            >
                                <List className="h-4 w-4" />
                            </Button>
                        </div>
                        <Button onClick={() => {
                            setSelectedPage(null);
                            setIsEditorOpen(true);
                        }}>
                            <Plus className="mr-2 h-4 w-4" /> Створити
                        </Button>
                    </div>
                </div>

                <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                        <Input
                            placeholder="Пошук..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="max-w-sm"
                            startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
                        />
                    </div>
                    <Button variant="outline">
                        <Filter className="mr-2 h-4 w-4" /> Фільтри
                    </Button>
                </div>

                <TabsContent value="pages" className="space-y-4">
                    <ScrollArea className="h-[600px]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Назва</TableHead>
                                    <TableHead>Slug</TableHead>
                                    <TableHead>Статус</TableHead>
                                    <TableHead>Останні зміни</TableHead>
                                    <TableHead>Автор</TableHead>
                                    <TableHead className="text-right">Дії</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {mockPages.map((page) => (
                                    <TableRow key={page.id}>
                                        <TableCell className="font-medium">{page.title}</TableCell>
                                        <TableCell>{page.slug}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-md ${getStatusColor(page.status)}`}>
                                                {page.status === "published" ? "Опубліковано" : "Чернетка"}
                                            </span>
                                        </TableCell>
                                        <TableCell>{new Date(page.lastModified).toLocaleString("uk-UA")}</TableCell>
                                        <TableCell>{page.author}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleEdit(page)}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDelete(page)}
                                            >
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollArea>
                </TabsContent>

                <TabsContent value="media" className="space-y-4">
                    <ScrollArea className="h-[600px]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Назва</TableHead>
                                    <TableHead>Тип</TableHead>
                                    <TableHead>Розмір</TableHead>
                                    <TableHead>Розміри</TableHead>
                                    <TableHead>Завантажено</TableHead>
                                    <TableHead className="text-right">Дії</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {mockMedia.map((media) => (
                                    <TableRow key={media.id}>
                                        <TableCell className="font-medium">{media.name}</TableCell>
                                        <TableCell>{media.type}</TableCell>
                                        <TableCell>{media.size}</TableCell>
                                        <TableCell>{media.dimensions || "-"}</TableCell>
                                        <TableCell>{new Date(media.uploadedAt).toLocaleString("uk-UA")}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="icon">
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon">
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollArea>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Налаштування CMS</CardTitle>
                            <CardDescription>
                                Налаштуйте основні параметри системи управління контентом
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Мова за замовчуванням</label>
                                <Select defaultValue="uk">
                                    <SelectTrigger>
                                        <SelectValue placeholder="Оберіть мову" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="uk">Українська</SelectItem>
                                        <SelectItem value="en">English</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Формат дати</label>
                                <Select defaultValue="dd.mm.yyyy">
                                    <SelectTrigger>
                                        <SelectValue placeholder="Оберіть формат" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="dd.mm.yyyy">DD.MM.YYYY</SelectItem>
                                        <SelectItem value="mm.dd.yyyy">MM.DD.YYYY</SelectItem>
                                        <SelectItem value="yyyy.mm.dd">YYYY.MM.DD</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <PageEditor
                isOpen={isEditorOpen}
                onClose={() => setIsEditorOpen(false)}
                page={selectedPage}
            />

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Видалення сторінки</AlertDialogTitle>
                        <AlertDialogDescription>
                            Ви впевнені, що хочете видалити сторінку "{pageToDelete?.title}"?
                            Цю дію неможливо відмінити.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Скасувати</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            <AlertTriangle className="mr-2 h-4 w-4" />
                            Видалити
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CMSPage; 